import Phaser from 'phaser';
import { PALETTE, paletteNumber } from '../data/palette.generated';
import { virtualInput } from '../input/VirtualInput';
import type { Direction } from '../logic/movement';

const DPAD_LABELS: Record<Direction, string> = {
  up: '^',
  down: 'v',
  left: '<',
  right: '>',
};

/**
 * Always-on-top HUD scene: virtual D-pad (four-way) plus A and B buttons.
 * Pointer input here only ever writes into the shared `virtualInput`
 * singleton — LibraryScene (or any future scene) decides what to do with
 * it, so this scene stays game-logic-free.
 */
export class UIScene extends Phaser.Scene {
  constructor() {
    super({ key: 'UIScene' });
  }

  create(): void {
    this.createDPad();
    this.createActionButtons();
    this.createPortrait();
  }

  private makeButton(
    x: number,
    y: number,
    size: number,
    label: string,
    onDown: () => void,
    onUp?: () => void
  ): void {
    const idleColor = paletteNumber(PALETTE.ROYAL_BLUE);
    const activeColor = paletteNumber(PALETTE.SOFT_VIOLET);

    const rect = this.add.rectangle(x, y, size, size, idleColor).setStrokeStyle(1, paletteNumber(PALETTE.DEEP_PLUM));
    rect.setInteractive({ useHandCursor: true });
    rect.setScrollFactor(0);

    this.add
      .text(x, y, label, {
        fontFamily: 'Press Start 2P',
        fontSize: '8px',
        color: PALETTE.CREAM,
      })
      .setOrigin(0.5)
      .setScrollFactor(0);

    rect.on('pointerdown', () => {
      rect.setFillStyle(activeColor);
      onDown();
    });
    const release = () => {
      rect.setFillStyle(idleColor);
      onUp?.();
    };
    rect.on('pointerup', release);
    rect.on('pointerout', release);
  }

  private createDPad(): void {
    const cell = 14;
    const originX = 8;
    const originY = 180 - 8 - cell * 3;

    const center = (col: number, row: number): [number, number] => [
      originX + col * cell + cell / 2,
      originY + row * cell + cell / 2,
    ];

    const [upX, upY] = center(1, 0);
    this.makeButton(
      upX,
      upY,
      cell,
      DPAD_LABELS.up,
      () => {
        virtualInput.setDirection('up', true);
      },
      () => {
        virtualInput.setDirection('up', false);
      }
    );

    const [leftX, leftY] = center(0, 1);
    this.makeButton(
      leftX,
      leftY,
      cell,
      DPAD_LABELS.left,
      () => {
        virtualInput.setDirection('left', true);
      },
      () => {
        virtualInput.setDirection('left', false);
      }
    );

    const [rightX, rightY] = center(2, 1);
    this.makeButton(
      rightX,
      rightY,
      cell,
      DPAD_LABELS.right,
      () => {
        virtualInput.setDirection('right', true);
      },
      () => {
        virtualInput.setDirection('right', false);
      }
    );

    const [downX, downY] = center(1, 2);
    this.makeButton(
      downX,
      downY,
      cell,
      DPAD_LABELS.down,
      () => {
        virtualInput.setDirection('down', true);
      },
      () => {
        virtualInput.setDirection('down', false);
      }
    );
  }

  private createActionButtons(): void {
    const size = 16;

    this.makeButton(266, 158, size, 'B', () => {
      virtualInput.pressB();
    });
    this.makeButton(296, 142, size, 'A', () => {
      virtualInput.pressA();
    });
  }

  private createPortrait(): void {
    // Small HUD portrait in the top-left corner — not interactive, just
    // uses the 96x96 placeholder portrait asset as a status-bar flourish.
    const portrait = this.add.image(4, 4, 'player-portrait');
    portrait.setOrigin(0, 0);
    portrait.setDisplaySize(20, 20);
    portrait.setScrollFactor(0);
  }
}
